import React, { useCallback, useState, useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Button, Paper, Typography, Snackbar, Alert, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import cookie from 'js-cookie';
import { useParams, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';

// Import our new context and actions
import {
    useDeficiencies,
    ACTIONS,
    DeficiencyProvider,
    MODAL_TYPES,
    getAssignmentMetadata,
} from './DeficiencyContext';

// Import getStatusChipProps for the sidebar
import { getStatusChipProps } from './constants';

//import the components
import SearchBar from './SearchBar';
import Buttons from './Buttons';
import ExportButton from './ExportButton';
import DeficiencyList from './DeficiencyList';
import StatusChangeModal from './Modals/StatusChangeModal';
import AssigneeModal from './Modals/AssigneeModal';
import DeferredCostModal from './Modals/DeferredCostModal';
import DueDateModal from './Modals/DueDateModal';
import RejectionReasonModal from './Modals/RejectionReasonModal';
import Breadcrumbs from '../../global/Components/Breadcrumbs';
// Import the global DeficiencySidebar component
import DeficiencySidebar from '../../global/Components/DeficiencySidebar/DeficiencySidebar';

import { useNotifications } from '../../../NotificationSystem';
import { usePermissions } from '../../../hooks/usePermissions';
import {
    canSeeCompanyFilter,
    canSeeDeferredCost,
    canSeeInternalOnlyFilter,
} from '../../../utils/roles';

/**
 * Styled button component for consistent action buttons throughout the UI
 */
const ResponsiveActionButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    borderRadius: 8,
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
        padding: '8px 12px',
        fontSize: '0.8rem',
        flex: 1,
        minWidth: 'unset',
    },
    [theme.breakpoints.up('sm')]: {
        padding: '10px 20px',
        fontSize: '0.875rem',
    },
}));

//temp hardcoded business id, in the future get from cookie
// const businessId = '51e3c940-0b73-11f0-a6ca-93004559f0a7';
/**
 * Wrapper component that provides the DeficiencyProvider context
 * to the main content component. This pattern allows for clean
 * context usage in the main component.
 *
 * @returns {React.ReactElement} Wrapped DeficiencyPageContent
 */
const DeficiencyPageWrapper = () => {
    // Get projectId from URL params
    const { projectId } = useParams();

    return (
        <DeficiencyProvider projectId={projectId}>
            <DeficiencyPageContent projectId={projectId} />
        </DeficiencyProvider>
    );
};

/**
 * Main deficiency management page component
 * Provides UI for viewing, filtering, and managing deficiencies
 * Serves as the central coordinator for deficiency-related operations
 *
 * @returns {React.ReactElement} Deficiency page UI
 */
const DeficiencyPageContent = ({ projectId }) => {
    const theme = useTheme();
    const location = useLocation();

    // Add snackbar state for notifications
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    // Rejection modal state
    // rejectionType: 'terminal' for dropdown override / SUBMITTED quick-reject
    // rejectionType: 'work' for workflow bounce-back from WORK_COMPLETED/INTERNALLY_SIGNED_OFF
    const [rejectionModal, setRejectionModal] = useState({
        open: false,
        deficiencyIds: [],
        rejectionType: 'terminal',
    });

    // When a rejection notification opens the sidebar, scroll to the rejection comment.
    const [scrollToRejection, setScrollToRejection] = useState(false);
    const [rejectionAttemptId, setRejectionAttemptId] = useState(null);

    // Get state, dispatch and computed values from context
    const {
        state,
        dispatch,
        filteredDeficiencies,
        updateDeficiency,
        batchUpdateDeficiencies,
        rejectTerminal,
        rejectWork,
        getImageUrl,
    } = useDeficiencies();

    const { markAsViewed, notifications } = useNotifications();
    const { getProjectRole } = usePermissions();
    const role = getProjectRole(projectId);
    const showCompanyFilter = canSeeCompanyFilter(role);
    const showInternalOnlyFilter = canSeeInternalOnlyFilter(role);
    const showDeferredCost = canSeeDeferredCost(role);

    // De-structure state for easier access
    const {
        isBatchMode,
        selectedItems,
        openModal,
        sortType,
        searchQuery,
        selectedDeficiency,
        isLoading,
        error: stateError,
        statusFilters,
        companyFilters,
        locationFilters,
        showCompleted,
        showRejected,
        internalOnlyFilter,
    } = state;

    // Handle navigation from notifications to highlight specific deficiency
    useEffect(() => {
        const highlightDeficiencyId = location.state?.highlightDeficiency;

        if (highlightDeficiencyId && state.deficiencies && state.deficiencies.length > 0) {
            const targetDeficiency = state.deficiencies.find(
                (def) => def.deficiencyId === highlightDeficiencyId
            );

            if (targetDeficiency) {
                // Mark related notifications as viewed - backend handles resolution automatically
                const currentNotifications = notifications;
                if (currentNotifications && Array.isArray(currentNotifications)) {
                    const relatedNotifications = currentNotifications
                        .filter(
                            (notification) =>
                                notification.entityType === 'DEFICIENCY' &&
                                notification.entityId === highlightDeficiencyId &&
                                !notification.viewed
                        )
                        .map((notification) => notification.notificationId);

                    if (relatedNotifications.length > 0) {
                        markAsViewed(relatedNotifications);
                    }
                }

                // Open the sidebar with the target deficiency
                setScrollToRejection(Boolean(location.state?.scrollToRejection));
                setRejectionAttemptId(location.state?.rejectionAttemptId || null);
                dispatch({
                    type: ACTIONS.SET_SELECTED_DEFICIENCY,
                    payload: targetDeficiency,
                });

                // Clear the navigation state to prevent re-triggering
                window.history.replaceState(null, '', window.location.pathname);
            }
        }
    }, [location.state, state.deficiencies, markAsViewed, dispatch]);

    // Handle status filter from dashboard navigation
    useEffect(() => {
        const filterByStatus = location.state?.filterByStatus;

        if (filterByStatus) {
            // Set the status filter
            dispatch({
                type: ACTIONS.SET_STATUS_FILTER,
                payload: filterByStatus,
            });

            // Keep default urgency sort - status filter is enough for context

            // Clear the navigation state to prevent re-triggering on refresh
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, [location.state, dispatch]);

    // Handle company filter from reports navigation
    useEffect(() => {
        const filterByCompany = location.state?.filterByCompany;

        if (filterByCompany) {
            // Set the company filter
            dispatch({
                type: ACTIONS.SET_COMPANY_FILTER,
                payload: {
                    companyId: filterByCompany.companyId,
                    companyName: filterByCompany.companyName,
                },
            });

            // Keep default grouping (urgency view)

            // Clear the navigation state to prevent re-triggering on refresh
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, [location.state, dispatch]);

    // Handle custom event to open deficiency sidebar (for notifications clicked while already on page)
    useEffect(() => {
        const handleOpenSidebar = (event) => {
            const {
                deficiencyId,
                notificationId,
                scrollToRejection: shouldScroll,
                rejectionAttemptId: attemptId,
            } = event.detail;

            if (deficiencyId && state.deficiencies && state.deficiencies.length > 0) {
                const targetDeficiency = state.deficiencies.find(
                    (def) => def.deficiencyId === deficiencyId
                );

                if (targetDeficiency) {
                    // Mark the specific notification as viewed - backend handles resolution automatically
                    if (notificationId) {
                        markAsViewed([notificationId]);
                    }

                    // Mark any other related notifications as viewed
                    // Get current notifications directly from context instead of dependency
                    const currentNotifications = notifications;
                    if (currentNotifications && Array.isArray(currentNotifications)) {
                        const relatedNotifications = currentNotifications
                            .filter(
                                (notification) =>
                                    notification.entityType === 'DEFICIENCY' &&
                                    notification.entityId === deficiencyId &&
                                    !notification.viewed &&
                                    notification.notificationId !== notificationId
                            )
                            .map((notification) => notification.notificationId);

                        if (relatedNotifications.length > 0) {
                            markAsViewed(relatedNotifications);
                        }
                    }

                    // Open the sidebar with the target deficiency
                    setScrollToRejection(Boolean(shouldScroll));
                    setRejectionAttemptId(attemptId || null);
                    dispatch({
                        type: ACTIONS.SET_SELECTED_DEFICIENCY,
                        payload: targetDeficiency,
                    });
                }
            }
        };

        window.addEventListener('openDeficiencySidebar', handleOpenSidebar);

        return () => {
            window.removeEventListener('openDeficiencySidebar', handleOpenSidebar);
        };
    }, [state.deficiencies, markAsViewed, dispatch]);

    /**
     * Open a modal by type
     * @param {string} modalType - Type of modal to open (from MODAL_TYPES)
     */
    const handleOpenModal = useCallback(
        (modalType) => {
            dispatch({
                type: ACTIONS.OPEN_MODAL,
                payload: modalType,
            });
        },
        [dispatch]
    );

    /**
     * Close the currently open modal
     */
    const handleCloseModal = useCallback(() => {
        dispatch({
            type: ACTIONS.CLOSE_MODAL,
        });
    }, [dispatch]);

    /**
     * Handle search query changes
     * @param {string} query - New search query
     */
    const handleSearch = useCallback(
        (query) => {
            dispatch({
                type: ACTIONS.SET_SEARCH_QUERY,
                payload: query,
            });
        },
        [dispatch]
    );

    /**
     * Change the sort type for deficiencies
     * @param {string} type - Sort type ('urgency', 'dueDate', 'dateCreated', 'deferredCost')
     */
    const handleSortChange = useCallback(
        (type) => {
            dispatch({
                type: ACTIONS.SET_SORT_TYPE,
                payload: type,
            });
        },
        [dispatch]
    );

    /**
     * Toggle the "Show Completed" filter
     */
    const handleToggleShowCompleted = useCallback(() => {
        dispatch({ type: ACTIONS.TOGGLE_SHOW_COMPLETED });
    }, [dispatch]);

    /**
     * Toggle the "Show Rejected" filter
     */
    const handleToggleShowRejected = useCallback(() => {
        dispatch({ type: ACTIONS.TOGGLE_SHOW_REJECTED });
    }, [dispatch]);

    /**
     * Toggle the "Internal Only" filter
     */
    const handleToggleInternalOnly = useCallback(() => {
        dispatch({ type: ACTIONS.TOGGLE_INTERNAL_ONLY_FILTER });
    }, [dispatch]);

    /**
     * Handle selecting a deficiency to view details
     * @param {Object} deficiency - The deficiency object to view
     */
    const handleDeficiencySelect = useCallback(
        (deficiency) => {
            if (!isBatchMode) {
                // Mark related notifications as viewed when deficiency is selected - backend handles resolution automatically
                if (notifications && Array.isArray(notifications)) {
                    const relatedNotifications = notifications
                        .filter(
                            (notification) =>
                                notification.entityType === 'DEFICIENCY' &&
                                notification.entityId === deficiency.deficiencyId &&
                                !notification.viewed
                        )
                        .map((notification) => notification.notificationId);

                    if (relatedNotifications.length > 0) {
                        markAsViewed(relatedNotifications);
                    }
                }

                dispatch({
                    type: ACTIONS.SET_SELECTED_DEFICIENCY,
                    payload: deficiency,
                });
            }
        },
        [dispatch, isBatchMode, notifications, markAsViewed]
    );

    /**
     * Handle closing the deficiency sidebar
     */
    const handleSidebarClose = useCallback(() => {
        setScrollToRejection(false);
        setRejectionAttemptId(null);
        dispatch({
            type: ACTIONS.CLEAR_SELECTED_DEFICIENCY,
        });
    }, [dispatch]);

    //This is the breadcrumb links for the Deficiencies page
    const breadcrumbLinks = [
        { path: '/projects', label: 'Projects' },
        { path: `/projects/${projectId}`, label: 'Project Dashboard' },
        { path: `/projects/${projectId}/deficiencies`, label: 'Deficiencies' },
    ];

    /**
     * Update selected items for batch operations
     * @param {Set<string>} newSelected - Set of selected item IDs
     */
    const handleSelectionChange = useCallback(
        (newSelected) => {
            dispatch({
                type: ACTIONS.UPDATE_SELECTIONS,
                payload: newSelected,
            });
        },
        [dispatch]
    );

    /**
     * Toggle batch mode on/off
     * @param {boolean} enabled - Whether batch mode should be enabled
     */
    const handleBatchModeChange = useCallback(
        (enabled) => {
            dispatch({
                type: ACTIONS.SET_BATCH_MODE,
                payload: enabled,
            });

            // Clear selections when turning off batch mode
            if (!enabled) {
                dispatch({
                    type: ACTIONS.CLEAR_SELECTIONS,
                });
            }
        },
        [dispatch]
    );

    /**
     * Clear all selected items
     */
    const handleClearSelections = useCallback(() => {
        dispatch({
            type: ACTIONS.CLEAR_SELECTIONS,
        });
    }, [dispatch]);

    /**
     * Handle closing the snackbar
     */
    const handleSnackbarClose = useCallback(() => {
        setSnackbar((prev) => ({
            ...prev,
            open: false,
        }));
    }, []);

    /**
     * Change status of one or multiple deficiencies
     * @param {string|string[]} deficiencyIdOrIds - ID or array of IDs to update
     * @param {string} newStatus - New status to set
     */
    const handleStatusChange = useCallback(
        async (deficiencyIdOrIds, newStatus) => {
            // Intercept 'Rejected' status to show the reason modal
            // Dropdown override is ALWAYS terminal rejection
            if (newStatus === 'Rejected') {
                const ids = Array.isArray(deficiencyIdOrIds)
                    ? deficiencyIdOrIds
                    : [deficiencyIdOrIds];
                setRejectionModal({
                    open: true,
                    deficiencyIds: ids,
                    rejectionType: 'terminal',
                });
                return;
            }

            try {
                // Get the current user ID
                const currentUserId = cookie.get('userId');

                // Update the status in the context first for immediate UI feedback
                dispatch({
                    type: ACTIONS.CHANGE_STATUS,
                    payload: {
                        ids: deficiencyIdOrIds,
                        newStatus,
                        userId: currentUserId,
                    },
                });

                if (Array.isArray(deficiencyIdOrIds) && deficiencyIdOrIds.length > 0) {
                    // Batch update via API
                    // Find a sample deficiency to get current status for batch updates
                    // No longer needed since status metadata is handled by the backend

                    // Status metadata is now handled by the backend
                    // No need to add extra metadata fields

                    // Include only the status in the batch update
                    await batchUpdateDeficiencies(deficiencyIdOrIds, {
                        status: newStatus,
                    });

                    // Show success notification
                    setSnackbar({
                        open: true,
                        message: `Successfully updated status for ${deficiencyIdOrIds.length} deficiencies`,
                        severity: 'success',
                    });

                    // Clear selections after batch operation
                    handleClearSelections();

                    // Clear selected deficiency to prevent sidebar from opening
                    dispatch({
                        type: ACTIONS.CLEAR_SELECTED_DEFICIENCY,
                    });
                } else if (deficiencyIdOrIds) {
                    // Single deficiency update via API
                    const currentDeficiency = state.deficiencies.find(
                        (d) => d.deficiencyId === deficiencyIdOrIds
                    );

                    if (!currentDeficiency) {
                        throw new Error(`Deficiency with ID ${deficiencyIdOrIds} not found`);
                    }

                    // Get status metadata without date fields
                    // Status metadata is now handled by the backend
                    // No need to add extra metadata fields

                    // Update the deficiency with the new status
                    await updateDeficiency(deficiencyIdOrIds, {
                        status: newStatus,
                    });

                    // Show success notification
                    setSnackbar({
                        open: true,
                        message: 'Status updated successfully',
                        severity: 'success',
                    });
                }
            } catch (error) {
                // Log error internally but avoid console for production
                setSnackbar({
                    open: true,
                    message: `Error: ${error.message || 'Failed to update status'}`,
                    severity: 'error',
                });
            }
        },
        [
            dispatch,
            state.deficiencies,
            batchUpdateDeficiencies,
            updateDeficiency,
            handleClearSelections,
        ]
    );

    /**
     * Handle final rejection confirmation with reason.
     * Uses ONLY rejectionType from modal state to route to correct endpoint.
     * NO status-based inference - endpoint is determined by which UI action opened the modal.
     * @param {string} reason - The rejection reason
     */
    const handleRejectionConfirm = async (reason) => {
        const { deficiencyIds, rejectionType } = rejectionModal;
        setRejectionModal((prev) => ({ ...prev, open: false }));

        // GUARDRAIL: rejectionType must be explicitly set
        if (!rejectionType || (rejectionType !== 'terminal' && rejectionType !== 'work')) {
            console.error('[DEV ERROR] Invalid rejectionType:', rejectionType);
            setSnackbar({
                open: true,
                message: 'Internal error: Invalid rejection type. Please try again.',
                severity: 'error',
            });
            return;
        }

        try {
            for (const id of deficiencyIds) {
                const deficiency = state.deficiencies.find((d) => d.deficiencyId === id);
                if (!deficiency) {
                    console.error(`Deficiency ${id} not found in state`);
                    continue;
                }

                if (rejectionType === 'work') {
                    // SAFETY CHECKS for work rejection
                    const status = deficiency.status;
                    if (status !== 'Work Completed' && status !== 'Internally Signed Off') {
                        setSnackbar({
                            open: true,
                            message: `Cannot reject work: deficiency is not in a reviewable state (current: ${status}).`,
                            severity: 'error',
                        });
                        return;
                    }
                    if (!deficiency.activeAttemptId) {
                        setSnackbar({
                            open: true,
                            message: 'Cannot reject work: no active attempt found. Please refresh.',
                            severity: 'error',
                        });
                        return;
                    }
                    // Bounce-back rejection: requires attemptId for stale check
                    await rejectWork(id, reason, deficiency.activeAttemptId);
                } else {
                    // Terminal rejection - allowed from any status, no checks needed
                    await rejectTerminal(id, reason);
                }
            }

            setSnackbar({
                open: true,
                message: `Successfully rejected ${deficiencyIds.length} item(s)`,
                severity: 'success',
            });

            if (deficiencyIds.length > 1) {
                handleClearSelections();
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: `Error rejecting: ${error.message || 'Failed to process rejection'}`,
                severity: 'error',
            });
        }
    };

    /**
     * Open rejection modal for work rejection (bounce-back).
     * Called when user clicks "Reject Work" from WORK_COMPLETED or INTERNALLY_SIGNED_OFF.
     * @param {string} deficiencyId - ID of deficiency to reject
     */
    const handleWorkRejection = useCallback((deficiencyId) => {
        setRejectionModal({
            open: true,
            deficiencyIds: [deficiencyId],
            rejectionType: 'work',
        });
    }, []);

    /**
     * Open rejection modal for terminal rejection.
     * Called when user clicks quick-reject from SUBMITTED status.
     * @param {string} deficiencyId - ID of deficiency to reject
     */
    const handleTerminalRejection = useCallback((deficiencyId) => {
        setRejectionModal({
            open: true,
            deficiencyIds: [deficiencyId],
            rejectionType: 'terminal',
        });
    }, []);

    /**
     * Assign a deficiency to a business
     * @param {string} deficiencyId - ID of deficiency to assign
     * @param {string} assignee - Name of business assignee
     * @param {string} businessId - Optional business ID for the assignee
     */
    const handleAssign = useCallback(
        (deficiencyId, assignee, businessId) => {
            // Get current user ID
            const currentUserId = cookie.get('userId');

            dispatch({
                type: ACTIONS.ASSIGN_DEFICIENCY,
                payload: {
                    id: deficiencyId,
                    assignee,
                    businessId,
                    editorId: currentUserId,
                },
            });
        },
        [dispatch]
    );

    /**
     * Assign multiple deficiencies to a business (batch operation)
     * @param {Object} data - Assignment data with assignee name and businessId
     * @returns {Promise<void>}
     */
    const handleBatchAssign = useCallback(
        async (data) => {
            const selectedIds = Array.from(selectedItems);

            if (selectedIds.length === 0) {
                setSnackbar({
                    open: true,
                    message: 'No items selected for assignment',
                    severity: 'warning',
                });
                return;
            }

            try {
                // Extract businessId from data
                const { businessId } = data;
                const currentUserId = cookie.get('userId');

                // Find a sample deficiency to get current status (for status change logic)
                const sampleDeficiency =
                    selectedIds.length > 0
                        ? state.deficiencies.find((d) => d.deficiencyId === selectedIds[0])
                        : null;

                // Find business object
                const business =
                    businessId && state.businesses
                        ? state.businesses.find((b) => b.businessId === businessId)
                        : null;

                // Use centralized helper to generate assignment metadata
                const assignmentMetadata = getAssignmentMetadata(
                    businessId,
                    business,
                    sampleDeficiency?.status,
                    currentUserId
                );

                // Use the batchUpdateDeficiencies function with proper metadata
                await batchUpdateDeficiencies(selectedIds, assignmentMetadata);

                // Update UI state for assigned deficiencies
                dispatch({
                    type: ACTIONS.ASSIGN_DEFICIENCY,
                    payload: {
                        ids: selectedIds,
                        assignee: data.assignee,
                        businessId: businessId,
                        editorId: currentUserId,
                    },
                });

                // Show success notification
                setSnackbar({
                    open: true,
                    message: `Successfully assigned ${selectedIds.length} deficiencies`,
                    severity: 'success',
                });

                // Clear selections and close modal
                handleClearSelections();
                handleCloseModal();

                // Clear selected deficiency to prevent sidebar from opening
                dispatch({
                    type: ACTIONS.CLEAR_SELECTED_DEFICIENCY,
                });
            } catch (error) {
                // Error handling without console.log for production
                setSnackbar({
                    open: true,
                    message: `Error: ${error.message || 'Failed to assign deficiencies'}`,
                    severity: 'error',
                });
            }
        },
        [
            batchUpdateDeficiencies,
            dispatch,
            handleClearSelections,
            handleCloseModal,
            selectedItems,
            state.deficiencies,
            state.businesses,
        ]
    );

    /**
     * Set deferred cost for multiple deficiencies (batch operation)
     * @param {number} cost - Cost value to set
     * @returns {Promise<void>}
     */
    const handleSetCost = useCallback(
        async (cost) => {
            const selectedIds = Array.from(selectedItems);

            if (selectedIds.length === 0) {
                setSnackbar({
                    open: true,
                    message: 'No items selected for cost update',
                    severity: 'warning',
                });
                return;
            }

            try {
                // Use the batchUpdateDeficiencies function
                await batchUpdateDeficiencies(selectedIds, {
                    deferredCost: cost,
                });

                // Update UI state for cost updates
                dispatch({
                    type: ACTIONS.SET_DEFERRED_COST,
                    payload: {
                        ids: selectedIds,
                        cost,
                    },
                });

                // Show success notification
                setSnackbar({
                    open: true,
                    message: `Successfully updated cost for ${selectedIds.length} deficiencies`,
                    severity: 'success',
                });

                // Clear selections and close modal
                handleClearSelections();
                handleCloseModal();

                // Clear selected deficiency to prevent sidebar from opening
                dispatch({
                    type: ACTIONS.CLEAR_SELECTED_DEFICIENCY,
                });
            } catch (error) {
                // Error handling without console.log for production
                setSnackbar({
                    open: true,
                    message: `Error: ${error.message || 'Failed to update cost'}`,
                    severity: 'error',
                });
            }
        },
        [batchUpdateDeficiencies, dispatch, handleClearSelections, handleCloseModal, selectedItems]
    );

    /**
     * Set due date for multiple deficiencies (batch operation)
     * @param {string} dueDate - Date string in YYYY-MM-DD
     * @returns {Promise<void>}
     */
    const handleSetDueDate = useCallback(
        async (dueDate) => {
            const selectedIds = Array.from(selectedItems);

            if (selectedIds.length === 0) {
                setSnackbar({
                    open: true,
                    message: 'No items selected for due date update',
                    severity: 'warning',
                });
                return;
            }

            try {
                await batchUpdateDeficiencies(selectedIds, { dueDate });

                dispatch({
                    type: ACTIONS.SET_DUE_DATE,
                    payload: { ids: selectedIds, dueDate },
                });

                setSnackbar({
                    open: true,
                    message: `Successfully updated due date for ${selectedIds.length} deficiencies`,
                    severity: 'success',
                });

                handleClearSelections();
                handleCloseModal();

                dispatch({
                    type: ACTIONS.CLEAR_SELECTED_DEFICIENCY,
                });
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: `Error: ${error.message || 'Failed to update due date'}`,
                    severity: 'error',
                });
            }
        },
        [batchUpdateDeficiencies, dispatch, handleClearSelections, handleCloseModal, selectedItems]
    );

    /**
     * Toggle a status in the multi-select filter (for menu interaction)
     */
    const handleToggleStatus = useCallback(
        (status) => {
            dispatch({ type: ACTIONS.TOGGLE_STATUS_FILTER, payload: status });
        },
        [dispatch]
    );

    /**
     * Toggle a company in the multi-select filter (for menu interaction)
     */
    const handleToggleCompany = useCallback(
        (company) => {
            dispatch({ type: ACTIONS.TOGGLE_COMPANY_FILTER, payload: company });
        },
        [dispatch]
    );

    /**
     * Toggle a location (floorplan + room) in the multi-select filter
     */
    const handleToggleLocation = useCallback(
        (location) => {
            dispatch({ type: ACTIONS.TOGGLE_LOCATION_FILTER, payload: location });
        },
        [dispatch]
    );

    /**
     * Remove ALL location filter entries for a given floorplan (chip delete for consolidated chip)
     */
    const handleRemoveFloorplanFilter = useCallback(
        (floorplanName) => {
            dispatch({ type: ACTIONS.CLEAR_FLOORPLAN_FILTER, payload: floorplanName });
        },
        [dispatch]
    );

    /**
     * Clear all active filters at once
     */
    const handleClearAllFilters = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ALL_FILTERS });
    }, [dispatch]);

    // Consolidate location filters into display chips
    // If a floorplan has 3+ rooms selected or the floorplan-level entry, show one "Floor: X" chip
    // Otherwise show individual room chips
    const locationChips = useMemo(() => {
        const byFloorplan = {};
        locationFilters.forEach((l) => {
            if (!byFloorplan[l.floorplanName]) byFloorplan[l.floorplanName] = [];
            byFloorplan[l.floorplanName].push(l);
        });

        const chips = [];
        Object.entries(byFloorplan).forEach(([fpName, entries]) => {
            const hasFloorplanLevel = entries.some((e) => e.roomName === null);
            if (hasFloorplanLevel || entries.length >= 3) {
                // Consolidate into a single floorplan chip
                chips.push({
                    key: `floor-${fpName}`,
                    label: `Floor: ${fpName}`,
                    onDelete: () => handleRemoveFloorplanFilter(fpName),
                });
            } else {
                // Show individual room chips
                entries.forEach((entry) => {
                    chips.push({
                        key: `location-${entry.floorplanName}-${entry.roomName || 'all'}`,
                        label:
                            entry.roomName === null
                                ? `Floor: ${entry.floorplanName}`
                                : `${entry.floorplanName} — ${entry.roomName}`,
                        onDelete: () => handleToggleLocation(entry),
                    });
                });
            }
        });
        return chips;
    }, [locationFilters, handleRemoveFloorplanFilter, handleToggleLocation]);

    // Shared styling for active filter chips
    const filterChipSx = {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        fontWeight: 500,
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
        height: { xs: '28px', sm: '32px' },
        borderRadius: '16px',
        boxShadow: '0 2px 4px rgba(22, 82, 240, 0.2)',
        '& .MuiChip-deleteIcon': {
            color: '#fff',
            opacity: 0.8,
            '&:hover': { color: '#fff', opacity: 1 },
        },
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: '0 3px 6px rgba(22, 82, 240, 0.3)',
        },
    };

    // Total count of individual filter selections
    const activeFilterCount =
        statusFilters.length +
        (showCompanyFilter ? companyFilters.length : 0) +
        locationFilters.length +
        (showInternalOnlyFilter && internalOnlyFilter ? 1 : 0) +
        (showCompleted ? 1 : 0) +
        (showRejected ? 1 : 0);

    return (
        <Box
            sx={{
                position: 'relative',
                minHeight: '100vh',
                width: '100%',
                maxWidth: '100vw',
                overflow: 'hidden',
                boxSizing: 'border-box',
            }}
        >
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <Typography variant="h6">Loading deficiencies...</Typography>
                </Box>
            ) : stateError ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <Typography variant="h6" color="error">
                        Error loading deficiencies: {stateError}
                    </Typography>
                </Box>
            ) : (
                <Grid
                    container
                    spacing={3}
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        pb: isBatchMode ? 10 : 3,
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                >
                    {/* Breadcrumbs and Grouping Buttons */}
                    <Grid size={{ xs: 12 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                width: '100%',
                                gap: { xs: 1, sm: 2 },
                            }}
                        >
                            <Box
                                sx={{
                                    marginRight: 'auto',
                                }}
                            >
                                <Breadcrumbs links={breadcrumbLinks} />
                            </Box>

                            {/* Better positioning of buttons on mobile */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                    alignSelf: { xs: 'stretch', sm: 'flex-end' },
                                    marginLeft: { sm: 'auto' },
                                    width: { xs: '100%', sm: 'auto' },
                                }}
                            >
                                <Buttons
                                    onSortChange={handleSortChange}
                                    currentSort={sortType}
                                    isBatchMode={isBatchMode}
                                    onBatchModeChange={handleBatchModeChange}
                                    deficiencies={state.deficiencies}
                                    statusFilters={statusFilters}
                                    companyFilters={companyFilters}
                                    locationFilters={locationFilters}
                                    onToggleStatus={handleToggleStatus}
                                    onToggleCompany={handleToggleCompany}
                                    onToggleLocation={handleToggleLocation}
                                    internalOnlyFilter={internalOnlyFilter}
                                    onToggleInternalOnly={handleToggleInternalOnly}
                                    showCompleted={showCompleted}
                                    onToggleShowCompleted={handleToggleShowCompleted}
                                    showRejected={showRejected}
                                    onToggleShowRejected={handleToggleShowRejected}
                                />
                                <ExportButton
                                    filteredDeficiencies={filteredDeficiencies}
                                    filters={{
                                        statusFilters,
                                        companyFilters,
                                        locationFilters,
                                        internalOnlyFilter,
                                        showCompleted,
                                        showRejected,
                                        searchQuery,
                                    }}
                                    getImageUrl={getImageUrl}
                                    projectId={projectId}
                                    showDeferredCost={showDeferredCost}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    {/* Search Bar - Full width */}
                    <Grid size={{ xs: 12 }}>
                        <SearchBar onSearch={handleSearch} initialValue={searchQuery} />
                    </Grid>

                    {/* Active Filters Row */}
                    {activeFilterCount > 0 && (
                        <Grid size={{ xs: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mt: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: theme.palette.grey[600],
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        fontWeight: 500,
                                    }}
                                >
                                    Active Filters:
                                </Typography>

                                {statusFilters.map((status) => (
                                    <Chip
                                        key={`status-${status}`}
                                        label={`Status: ${status}`}
                                        onDelete={() => handleToggleStatus(status)}
                                        deleteIcon={<CloseIcon sx={{ fontSize: '1.1rem' }} />}
                                        sx={filterChipSx}
                                    />
                                ))}

                                {showCompanyFilter &&
                                    companyFilters.map((company) => (
                                        <Chip
                                            key={`company-${company.companyId}`}
                                            label={`Company: ${company.companyName}`}
                                            onDelete={() => handleToggleCompany(company)}
                                            deleteIcon={<CloseIcon sx={{ fontSize: '1.1rem' }} />}
                                            sx={filterChipSx}
                                        />
                                    ))}

                                {locationChips.map((chip) => (
                                    <Chip
                                        key={chip.key}
                                        label={chip.label}
                                        onDelete={chip.onDelete}
                                        deleteIcon={<CloseIcon sx={{ fontSize: '1.1rem' }} />}
                                        sx={filterChipSx}
                                    />
                                ))}

                                {showInternalOnlyFilter && internalOnlyFilter && (
                                    <Chip
                                        label="Internal Only"
                                        onDelete={handleToggleInternalOnly}
                                        deleteIcon={<CloseIcon sx={{ fontSize: '1.1rem' }} />}
                                        sx={filterChipSx}
                                    />
                                )}

                                {showCompleted && (
                                    <Chip
                                        label="Show Completed"
                                        onDelete={handleToggleShowCompleted}
                                        deleteIcon={<CloseIcon sx={{ fontSize: '1.1rem' }} />}
                                        sx={filterChipSx}
                                    />
                                )}

                                {showRejected && (
                                    <Chip
                                        label="Show Rejected"
                                        onDelete={handleToggleShowRejected}
                                        deleteIcon={<CloseIcon sx={{ fontSize: '1.1rem' }} />}
                                        sx={filterChipSx}
                                    />
                                )}

                                {activeFilterCount >= 2 && (
                                    <Typography
                                        variant="body2"
                                        onClick={handleClearAllFilters}
                                        sx={{
                                            color: theme.palette.primary.main,
                                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            ml: 0.5,
                                            '&:hover': {
                                                textDecoration: 'underline',
                                                color: theme.palette.primary.dark,
                                            },
                                        }}
                                    >
                                        Clear All
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    )}

                    {/* Main Deficiency List */}
                    <Grid size={{ xs: 12 }}>
                        {filteredDeficiencies.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 8,
                                    px: 2,
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: theme.palette.grey[500],
                                        fontWeight: 500,
                                        mb: 1,
                                    }}
                                >
                                    No deficiencies found
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.grey[400] }}>
                                    {activeFilterCount > 0 || searchQuery
                                        ? 'Try adjusting your filters or search query.'
                                        : 'No deficiencies have been created yet.'}
                                </Typography>
                                {activeFilterCount > 0 && (
                                    <Button
                                        variant="text"
                                        onClick={handleClearAllFilters}
                                        sx={{
                                            mt: 2,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Clear All Filters
                                    </Button>
                                )}
                            </Box>
                        ) : (
                            <DeficiencyList
                                deficiencies={filteredDeficiencies}
                                sortType={sortType}
                                isBatchMode={isBatchMode}
                                selectedItems={selectedItems}
                                onSelectionChange={handleSelectionChange}
                                onStatusChange={handleStatusChange}
                                onAssign={handleAssign}
                                onDeficiencySelect={handleDeficiencySelect}
                                onTerminalRejection={handleTerminalRejection}
                                onWorkRejection={handleWorkRejection}
                            />
                        )}
                    </Grid>
                </Grid>
            )}

            {/* Floating Batch Action Bar - Optimized for mobile */}
            {isBatchMode && (
                <Paper
                    elevation={3}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 3,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 2 },
                        backgroundColor: 'white',
                        zIndex: 1000,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                        maxWidth: { xs: 'calc(100% - 32px)', sm: 'none' },
                    }}
                >
                    {/* Top row on mobile with cancel and count */}
                    <Box
                        sx={{
                            display: { xs: 'flex', sm: 'none' },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            mb: 1,
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={() => {
                                handleBatchModeChange(false);
                            }}
                            sx={{
                                py: { xs: 0.5 },
                                textTransform: 'none',
                                borderRadius: 8,
                            }}
                        >
                            Cancel
                        </Button>

                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.grey[700],
                                display: 'flex',
                                alignItems: 'center',
                                mx: 1,
                            }}
                        >
                            {selectedItems.size} selected
                        </Typography>
                    </Box>

                    {/* Desktop layout */}
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={() => {
                                handleBatchModeChange(false);
                            }}
                            sx={{
                                textTransform: 'none',
                                borderRadius: 8,
                                padding: '10px 20px',
                                fontWeight: 500,
                            }}
                        >
                            Cancel
                        </Button>

                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.grey[700],
                                display: 'flex',
                                alignItems: 'center',
                                mx: 1,
                            }}
                        >
                            {selectedItems.size}
                        </Typography>
                    </Box>

                    {/* Single set of responsive action buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            width: { xs: '100%', sm: 'auto' },
                        }}
                    >
                        <ResponsiveActionButton
                            variant="contained"
                            color="primary"
                            disabled={selectedItems.size === 0}
                            onClick={() => handleOpenModal(MODAL_TYPES.STATUS)}
                        >
                            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Status</Box>
                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Change Status</Box>
                        </ResponsiveActionButton>

                        <ResponsiveActionButton
                            variant="contained"
                            color="primary"
                            disabled={selectedItems.size === 0}
                            onClick={() => handleOpenModal(MODAL_TYPES.ASSIGNEE)}
                        >
                            Assign
                        </ResponsiveActionButton>

                        {showDeferredCost && (
                            <ResponsiveActionButton
                                variant="contained"
                                color="primary"
                                disabled={selectedItems.size === 0}
                                onClick={() => handleOpenModal(MODAL_TYPES.COST)}
                            >
                                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Cost</Box>
                                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                    Deferred Cost
                                </Box>
                            </ResponsiveActionButton>
                        )}

                        <ResponsiveActionButton
                            variant="contained"
                            color="primary"
                            disabled={selectedItems.size === 0}
                            onClick={() => handleOpenModal(MODAL_TYPES.DUE_DATE)}
                        >
                            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Due</Box>
                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Set Due Date</Box>
                        </ResponsiveActionButton>
                    </Box>
                </Paper>
            )}

            {/* Modals */}
            <StatusChangeModal
                open={openModal === MODAL_TYPES.STATUS}
                onClose={handleCloseModal}
                onSubmit={handleStatusChange}
                selectedItems={selectedItems}
            />
            <AssigneeModal
                open={openModal === MODAL_TYPES.ASSIGNEE}
                onClose={handleCloseModal}
                onSubmit={handleBatchAssign}
            />
            <DeferredCostModal
                open={openModal === MODAL_TYPES.COST}
                onClose={handleCloseModal}
                onSubmit={handleSetCost}
                selectedCount={selectedItems.size}
            />

            <DueDateModal
                open={openModal === MODAL_TYPES.DUE_DATE}
                onClose={handleCloseModal}
                onSubmit={handleSetDueDate}
                selectedCount={selectedItems.size}
            />

            <RejectionReasonModal
                open={rejectionModal.open}
                onClose={() => setRejectionModal((prev) => ({ ...prev, open: false }))}
                onConfirm={handleRejectionConfirm}
                title={
                    rejectionModal.deficiencyIds.length > 1
                        ? `Reject ${rejectionModal.deficiencyIds.length} Items`
                        : rejectionModal.rejectionType === 'work'
                          ? 'Reject Work'
                          : 'Reject Deficiency'
                }
            />

            {/* Global Deficiency Detail Sidebar */}
            <DeficiencySidebar
                open={Boolean(selectedDeficiency)}
                deficiency={selectedDeficiency}
                onClose={handleSidebarClose}
                getStatusChipProps={getStatusChipProps}
                onStatusChange={handleStatusChange}
                scrollToRejection={scrollToRejection}
                rejectionAttemptId={rejectionAttemptId}
            />

            {/* Notification Snackbar - Better positioned for mobile */}
            <NotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleSnackbarClose}
            />
        </Box>
    );
};

// Create reusable notification component
const NotificationSnackbar = ({ open, message, severity, onClose }) => (
    <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
            position: 'fixed',
            bottom: { xs: 70, sm: 20 },
            right: { xs: 0, sm: 20 },
            left: { xs: 0, sm: 'auto' },
            transform: 'none',
            maxWidth: { xs: '100%', sm: '300px' },
            width: { xs: '100%', sm: 'auto' },
        }}
    >
        <Alert
            onClose={onClose}
            severity={severity}
            sx={{
                width: '100%',
                boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                borderRadius: { xs: 0, sm: 1 },
            }}
        >
            {message}
        </Alert>
    </Snackbar>
);

// Export the wrapper component
export default DeficiencyPageWrapper;
